import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Card from "shared/components/UIElements/Card";
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import Link from "shared/components/FormElements/Link";
import {
  VALIDATOR_MINLENGTH,
  VALIDATOR_REQUIRE,
  VALIDATOR_EMAIL,
  VALIDATOR_PASSWORD_STRONG,
} from "shared/util/validators";
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { getDeviceId } from "shared/util/auth/device-id";
import { getCurrentLanguage } from "shared/utility/lang";
import { isInternationalTheme } from "config/ConfigUtils";
import { trackSuiteEvent } from "shared/util/track";
import { measureOpenAIRegistrationCompleted } from "shared/util/openai-ads";
import { getUtmParams } from "shared/util/utm";
import DemoLoginCard from "./components/DemoLoginCard";
import BookDemoSection from "./components/BookDemoSection";
import "./Auth.css";

const Auth = () => {
  let language = getCurrentLanguage();
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showDemoLogin = isInternationalTheme();

  const { isLoading, sendRequest } = useHttpClient();
  const [loginFailed, setLoginFailed] = useState(false);
  const [failedMessage1, setFailedMessage1] = useState("Error");
  const [failedMessage2, setFailedMessage2] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const [formState, inputHandler] = useForm(
    {
      name: {
        value: "",
        isValid: false,
      },
      email: {
        value: "",
        isValid: false,
      },
      password: {
        value: "",
        isValid: false,
      },
    },
    false,
  );

  const onHandleInput = (event) => {
    event.preventDefault();
    setLoginFailed(false);
  };

  const authSubmitHandler = async (event) => {
    event.preventDefault();

    trackSuiteEvent("Signup — Create Account (Submit)", "button_click");

    //
    if (!termsAccepted || !privacyAccepted) {
      setFailedMessage1(t("auth.pleaseAcceptTermsAndPrivacy"));
      setFailedMessage2("");
      setLoginFailed(true);
      return;
    }
    let language = getCurrentLanguage();
    try {
      const deviceId = getDeviceId();
      const utmData = getUtmParams();

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/signup`,
        "POST",
        JSON.stringify({
          name: formState.inputs.name.value,
          email: formState.inputs.email.value,
          initpassword: formState.inputs.password.value,
          deviceId: deviceId,
          language: language,
          ...(utmData && { utm: utmData }),
        }),
        {
          "Content-Type": "application/json",
        },
      );

      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18077551564/9kEgCIDY6bQcEMyXhqxD",
        });
      }
      await measureOpenAIRegistrationCompleted({
        email: formState.inputs.email.value,
        externalId: responseData.clientId,
        name: formState.inputs.name.value,
      });

      await auth.login(responseData.clientId, responseData.token);

      if (isInternationalTheme()) {
        navigate("/dashboard");
      } else {
        navigate("/users");
      }
    } catch (err) {
      const firstDotIndex = err?.message?.indexOf(".");
      if (firstDotIndex !== -1 && err.code === 403) {
        setFailedMessage1(err?.message?.slice(0, firstDotIndex + 1));
        setFailedMessage2(err?.message?.slice(firstDotIndex + 1).trim());
      } else {
        setFailedMessage1(err?.message);
        setFailedMessage2("");
      }
      document.getElementById("email").focus();
      setLoginFailed(true);
    }
  };

  return (
    <React.Fragment>
      <div className="auth-page-container">
        <div className={`auth-cards-wrapper ${showDemoLogin ? "auth-cards-wrapper--with-demo" : ""}`}>
          {/* Sign Up Card */}
          <Card className="authentication" style={{ minWidth: "400px" }}>
            <h2 style={{ marginTop: "5px" }}>{t("userSignup")}</h2>
            <hr />
            <form onSubmit={authSubmitHandler}>
              <Input
                element="input"
                id="name"
                type="name"
                label={t("name")}
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                onChange={onHandleInput}
                disabled={isLoading}
                dontShowError={true}
              />
              <Input
                element="input"
                id="email"
                type="email"
                label={t("email")}
                validators={[VALIDATOR_EMAIL()]}
                onInput={inputHandler}
                onChange={onHandleInput}
                disabled={isLoading}
                dontShowError={true}
              />
              <Input
                element="input"
                id="password"
                type="password"
                label={t("password")}
                validators={[VALIDATOR_PASSWORD_STRONG()]}
                onInput={inputHandler}
                onChange={onHandleInput}
                disabled={isLoading}
                autoComplete={"new-password"}
                dontShowError={true}
                errorText={t("passwordRequirements")}
              />

              <div style={{ textAlign: "left", marginBottom: "1rem", marginTop: "2rem" }}>
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={() => {
                    setTermsAccepted(!termsAccepted);
                    setLoginFailed(false);
                  }}
                />
                <label htmlFor="terms">
                  {language === "tr" && (
                    <React.Fragment>
                      <a href="/terms" target="_blank" rel="noopener noreferrer">
                        {t("acceptTerms.termsOfService")}
                      </a>
                      {t("acceptTerms.iAcceptThe")}
                    </React.Fragment>
                  )}
                  {language === "en" && (
                    <React.Fragment>
                      {t("acceptTerms.iAcceptThe")}
                      <Link to="/terms">{t("acceptTerms.termsOfService")}.</Link>
                    </React.Fragment>
                  )}
                </label>
              </div>

              <div style={{ textAlign: "left", marginBottom: "2rem" }}>
                <input
                  type="checkbox"
                  id="privacy"
                  checked={privacyAccepted}
                  onChange={() => {
                    setPrivacyAccepted(!privacyAccepted);
                    setLoginFailed(false);
                  }}
                />
                <label htmlFor="privacy">
                  {language === "tr" && (
                    <React.Fragment>
                      <a href="/privacy" target="_blank" rel="noopener noreferrer">
                        {t("acceptPrivacy.privacyPolicy")}
                      </a>
                      {t("acceptPrivacy.iAcceptThe")}
                    </React.Fragment>
                  )}
                  {language === "en" && (
                    <React.Fragment>
                      {t("acceptPrivacy.iAcceptThe")}
                      <Link to="/privacy">{t("acceptPrivacy.privacyPolicy")}.</Link>
                    </React.Fragment>
                  )}
                </label>
              </div>

              <Button type="submit" isLoading={isLoading} disabled={!formState.isValid || isLoading}>
                {t("submit")}
              </Button>
              {loginFailed && (
                <div className="login-failed">
                  <p>{failedMessage1}</p>
                  <p>{failedMessage2}</p>
                </div>
              )}
              <Link
                to="/login"
                styles={{ float: "left", fontWeight: "bold" }}
                onClick={() => trackSuiteEvent("Signup — Go to Login", "button_click")}
              >
                {t("userLogin")}
              </Link>
              <Link
                to="/reset-password"
                styles={{ float: "right", fontWeight: "bold" }}
                onClick={() => trackSuiteEvent("Signup — Reset Password", "button_click")}
              >
                {t("resetPassword")}
              </Link>
            </form>
          </Card>

          {/* Demo Login Card */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {showDemoLogin && <DemoLoginCard />}
            {showDemoLogin && <BookDemoSection eventName="Signup — Book a Demo Call" />}
          </div>
        </div>

        {/* Book a Demo Call Section */}
      </div>
    </React.Fragment>
  );
};

export default Auth;
